import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRbac } from "../../context";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import { Button } from "../../components/ui/button";
import { toast } from "react-hot-toast";
import { getCandidateLeads, type CandidateLead } from "../../services/candidateLeadsApi";
import { Phone, TrendingUp, Loader2 } from "lucide-react";

const ITEMS_PER_PAGE = 10;

const formatLeadType = (type?: CandidateLead["type"]) => {
  if (!type) return "N/A";
  switch (type) {
    case "company":
      return "Company";
    case "jobseeker":
      return "Job Seeker";
    case "recruter":
      return "Recruiter";
    default:
      return type;
  }
};

const formatDate = (value?: string) => {
  if (!value) return "N/A";
  try {
    return new Date(value).toLocaleDateString("en-IN");
  } catch {
    return value;
  }
};

const LeadsPage = () => {
  const { user } = useRbac();

  const [leads, setLeads] = useState<CandidateLead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      // Backend endpoint is ADMIN-only; if role changes client-side we avoid spamming requests.
      if (user?.role !== "ADMIN") {
        setLeads([]);
        setTotalCount(0);
        setTotalPages(1);
        return;
      }

      const response = await getCandidateLeads({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });

      const payload = response?.data;
      if (!payload) {
        setLeads([]);
        setTotalCount(0);
        setTotalPages(1);
        return;
      }

      setLeads(payload.leads ?? []);
      setTotalCount(payload.totalCount ?? 0);
      setTotalPages(payload.totalPages ?? 1);
      setCurrentPage(payload.currentPage ?? currentPage);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("Failed to load leads:", err);
      toast.error(err?.message || "Failed to load leads");
      setLeads([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, user?.role]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const startIndex = totalCount > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);

  const pages = useMemo(() => {
    const result: (number | "ellipsis-start" | "ellipsis-end")[] = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) result.push(i);
      return result;
    }

    result.push(1);

    if (currentPage <= 4) {
      for (let i = 2; i <= 5; i++) result.push(i);
      result.push("ellipsis-end");
      result.push(totalPages);
      return result;
    }

    if (currentPage >= totalPages - 3) {
      result.push("ellipsis-start");
      for (let i = totalPages - 4; i <= totalPages; i++) result.push(i);
      return result;
    }

    result.push("ellipsis-start");
    result.push(currentPage - 1);
    result.push(currentPage);
    result.push(currentPage + 1);
    result.push("ellipsis-end");
    result.push(totalPages);
    return result;
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    // Table stays in same layout; scroll to keep pagination context.
    const el = document.getElementById("leads-table");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="w-full p-2 sm:p-4">
      <Card className="shadow-sm border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Phone className="h-5 w-5 text-orange-600" />
            Leads
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-14 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : leads.length > 0 ? (
            <>
              <div id="leads-table" className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold text-gray-700">Name</TableHead>
                      <TableHead className="font-semibold text-gray-700">Email</TableHead>
                      <TableHead className="font-semibold text-gray-700">Mobile</TableHead>
                      <TableHead className="font-semibold text-gray-700">Type</TableHead>
                      <TableHead className="font-semibold text-gray-700">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead._id} className="hover:bg-gray-50/50 transition-colors">
                        <TableCell className="font-medium text-gray-900">
                          {lead.name || "N/A"}
                        </TableCell>
                        <TableCell>{lead.email || "N/A"}</TableCell>
                        <TableCell>{lead.mobile || "N/A"}</TableCell>
                        <TableCell>
                          <span className="px-2 py-[2px] text-[10px] uppercase tracking-wide rounded-full bg-gray-200 text-gray-700">
                            {formatLeadType(lead.type)}
                          </span>
                        </TableCell>
                        <TableCell>{formatDate(lead.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-sm text-gray-600">
                  Showing {startIndex} to {endIndex} of {totalCount} results
                </div>

                {totalPages > 1 && (
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage - 1);
                          }}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>

                      {pages.map((p, idx) => {
                        if (p === "ellipsis-start" || p === "ellipsis-end") {
                          return (
                            <PaginationItem key={`${p}-${idx}`}>
                              <span className="px-2 text-gray-400">...</span>
                            </PaginationItem>
                          );
                        }

                        return (
                          <PaginationItem key={p}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePageChange(p);
                              }}
                              isActive={currentPage === p}
                              className="cursor-pointer"
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage + 1);
                          }}
                          className={
                            currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"
                          }
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </div>
            </>
          ) : (
            <Card className="border-2 border-dashed border-gray-300/60 bg-white/40 backdrop-blur-sm">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200 shadow-inner">
                  <TrendingUp className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="mb-2 text-lg font-bold text-gray-900">No leads found</h3>
                <p className="max-w-sm text-sm font-medium text-gray-600">
                  There are no candidate leads to display right now.
                </p>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentPage(1);
                      fetchLeads();
                    }}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default LeadsPage;

